import { useState, useEffect, useContext } from 'react';
import User from '../../../hooks/User';
import useUpdateUserProfile from '../../../hooks/useUpdateUserProfile';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { UserContext } from '../../../hooks/UserContext';

export default function UserDatabase({
  isSuperuser,
  users,
  setUsers,
  sortColumn,
  setSortColumn,
  sortDirection,
  setSortDirection,
  setMessage,
  setActionModal,
  setActionSuccess,
  handleSwitchTableView,
}) {
  const [loadingUserActive, setLoadingUserActive] = useState(null);
  const [loadingUserAdmin, setLoadingUserAdmin] = useState(null);
  const [statusColumn, setStatusColumn] = useState("none"); // "user__is_active" or "user__last_login" or "none"
  const [editableCell, setEditableCell] = useState(null);
  const [editableCellValue, setEditableCellValue] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showDeactivatedAccounts, setShowDeactivatedAccounts] = useState(true);
  const [deactivatedAccountsLoading, setDeactivatedAccountsLoading] = useState(false);
  const [copyEmailsLoading, setCopyEmailsLoading] = useState(false);

  const { toggleAdmin, toggleActive, updateUserProfile } = User(); // update other users
  const { updateProfileWithoutPush } = useUpdateUserProfile(); // update self (active user)
  const { user } = useContext(UserContext);

  const handleToggleAdmin = async (user_id, currentStatus) => {
    const newStatus = !currentStatus; // flip the current status
    setLoadingUserAdmin(user_id); // disable admin status button for current user
    setActionModal(false);
    setActionSuccess(false);
    setMessage("");
    try {
      const result = await toggleAdmin(user_id, newStatus); // send is_active toggle request
      setUsers(users.map(user =>
        user.user__id === user_id
          ? { ...user, user__is_staff: result.is_staff }
          : user
      ));
      setActionSuccess(true);
      setMessage(`User admin status updated successfully!`);
    } catch (error) {
      console.error("Failed to toggle admin status", error);
      setActionSuccess(false);
      setMessage("Failed to toggle admin status.");
    } finally {
      setLoadingUserAdmin(null);
      setActionModal(true);
    }
  };

  const handleToggleActive = async (user_id, currentStatus) => {
    const newStatus = !currentStatus; // flip the current status
    setLoadingUserActive(user_id); // disable active status button for current user
    setActionModal(false);
    setActionSuccess(false);
    setMessage("");
    try {
      const result = await toggleActive(user_id, newStatus); // send is_active toggle request
      setUsers(users.map(user =>
        user.user__id === user_id
          ? { ...user, user__is_active: result.is_active }
          : user
      ));
      setActionSuccess(true);
      setMessage(`User active status updated successfully!`);
    } catch (error) {
      console.error("Failed to toggle active status", error);
      setActionSuccess(false);
      setMessage("Failed to toggle active status.");
    } finally {
      setLoadingUserActive(null);
      setActionModal(true);
    }
  };

  const handleSort = (column) => {
    if (column !== "user__is_active" && column !== "user__last_login") {
      setStatusColumn("none");
    }
    if (column === sortColumn) {
      sortDirection === "asc" ? setSortDirection("desc") : setSortDirection("asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  }

  const handleStatusSort = (column) => {
    setStatusColumn(column);
    handleSort(column);
  }

  const displaySortArrow = (column) => {
    if (sortColumn !== column) {
      return String.fromCharCode(8597); // bidirectional arrow
    }

    return sortDirection === "asc" ? String.fromCharCode(8593) : String.fromCharCode(8595);
  }

  const displayLastLogin = (timestamp) => {
    if (timestamp) {
      const lastLogin = new Date(timestamp);
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: false,
      }).format(lastLogin);
    } else {
      return "N/A";
    }
  }

  const renderEditableCell = (i, field, value, user_id) => {
    const key = `${i}_${field}`;

    return (
      <td className="dashboard-table-td" onMouseEnter={() => { handleEnterEditableCell(key, value) }} onMouseLeave={() => { handleExitEditableCell() }} onClick={e => handleCellClick(e, key)}>
        {editableCell === key && editing ? (
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            ref={editingRef}
          >
            <input
              value={editableCellValue}
              onChange={e => setEditableCellValue(e.target.value)}
              style={{ width: '80%', caretColor: 'auto' }}
            />
            <button
              onClick={() => handleConfirmEdit(user_id, field, editableCellValue)}
              style={{ cursor: 'pointer' }}
            >
              {editLoading ? (
                <div className="dashboard-spinner"></div>
              ) : ('confirm')}
            </button>
          </div>
        ) : editableCell === key ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{value}</span>
            <span
              onClick={() => setEditing(true)}
              style={{ opacity: 0.4, cursor: 'pointer' }}
              className="edit-button"
            >
              edit
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{value}</span>
            <span style={{ visibility: "hidden" }}>
              edit
            </span>
          </div>
        )}
      </td>
    );
  };

  // exit cell editing state with click outside of the cell
  const handleOutsideClick = () => {
    setEditableCell(null);
    setEditableCellValue(null);
    setEditing(false);
  }

  // clicks elsewhere within editing cell are allowed (don't exit editing state)
  const handleCellClick = (event, key) => {
    if (editableCell === key) {
      event.stopPropagation();
    }
  }
  const editingRef = useOutsideClick(handleOutsideClick);

  // disable mouse-movement-based handlers when editing a cell
  const handleEnterEditableCell = (key, value) => {
    if (!editing) {
      setEditableCell(key);
      setEditableCellValue(value);
    }
  }
  const handleExitEditableCell = () => {
    if (!editing) {
      setEditableCell(null);
      setEditableCellValue(null);
    }
  }

  const handleConfirmEdit = async (user_id, field, newValue) => {
    setEditLoading(true);
    setActionModal(false);
    setActionSuccess(false);
    setMessage("");
    try {
      const result = await updateUserProfile(user_id, { [field]: newValue });
      setUsers(users.map(user =>
        user.user__id === user_id
          ? { ...user, [field]: result.userProfile[field] }
          : user
      ));
      setActionSuccess(true);
      setMessage(`User updated successfully!`);
    } catch (error) {
      console.error("Failed to update user profile", error);
      setActionSuccess(false);
      setMessage("Failed to update user.");
    } finally {
      setEditing(false);
      setActionModal(true);
      setEditLoading(false);
    }
  }

  const handleToggleDeactivatedAccounts = async () => {
    setDeactivatedAccountsLoading(true);
    try {
      const toggleValue = !showDeactivatedAccounts;
      const success = await updateProfileWithoutPush({ ...user, show_deactivated_accounts: toggleValue });
      if (success) {
        setShowDeactivatedAccounts(toggleValue);
        user.show_deactivated_accounts = toggleValue;
      } else {
        setActionSuccess(false);
        setMessage(`Error occurred while updating table settings in profile.`);
        setActionModal(true);
      }
    } catch (error) {
      setActionSuccess(false);
      setMessage(`Error occurred while updating table settings in profile.`);
      setActionModal(true);
    } finally {
      setDeactivatedAccountsLoading(false);
    }
  }

  const exportUsersCSV = (users, showDeactivatedAccounts) => {
    if (users.length <= 0) {
      return;
    }

    const columnOrder = ['user__email', 'name', 'news_organization', 'user__is_staff', 'user__is_active', 'user__last_login'];
    const headerMap = {
      user__email: 'Email',
      name: 'Name',
      news_organization: 'News Organization',
      user__is_staff: 'Admin',
      user__is_active: 'Active',
      user__last_login: 'Last Activity',
    };

    const escapeCSVData = (value) => {
      if (value == null) return '';
      const str = String(value);
      if (str.includes('"') || str.includes(',') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const formatColumnData = (key, value) => {
      switch (key) {
        case 'user__last_login':
          return displayLastLogin(value);
        default:
          return value;
      }
    }

    const headers = columnOrder.map(key => headerMap[key]).join(',');

    const filteredData = showDeactivatedAccounts
      ? users
      : users.filter(row => row['user__is_active']);
    const rows = filteredData.map(row =>
      columnOrder.map(key => escapeCSVData(formatColumnData(key, row[key]))).join(',')
    );

    let csv = [headers, ...rows].join('\n'); // convert array to newline-separated string

    // create CSV file and temporary link to trigger download
    const CSVFile = new Blob([csv], { type: "text/csv" });
    const temp_link = document.createElement('a');
    let deactivated;
    if (showDeactivatedAccounts) {
      deactivated = 'All';
    } else {
      deactivated = 'ActiveOnly';
    }
    temp_link.download = `AITipSheetsUsers(${deactivated})${new Date().toLocaleDateString()}.csv`;
    const url = window.URL.createObjectURL(CSVFile);
    temp_link.href = url;
    document.body.appendChild(temp_link);
    temp_link.click();
    document.body.removeChild(temp_link);
  }

  const copyEmails = (users, showDeactivatedAccounts) => {
    setCopyEmailsLoading(true);
    setActionModal(false);
    setActionSuccess(false);
    setMessage("");
    try {
      const filteredData = showDeactivatedAccounts
        ? users
        : users.filter(row => row['user__is_active']);
      navigator.clipboard.writeText(filteredData.map(row => row['user__email']).join(','));
      setActionSuccess(true);
      setMessage(`Copied ${showDeactivatedAccounts ? 'all (including inactive)' : 'active'} users' emails to clipboard!`);
    } catch (error) {
      console.error("Failed to copy emails to clipboard", error);
      setActionSuccess(false);
      setMessage("Failed to copy emails to clipboard.");
    } finally {
      setActionModal(true);
      setCopyEmailsLoading(false);
    }
  }

  useEffect(() => {
    setShowDeactivatedAccounts(user.show_deactivated_accounts);
  }, [user]);

  // note: this doesn't need to be an effect
  useEffect(() => {
    const sortUsers = () => {
      const newUsers = [...users];

      newUsers.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];

        if (sortColumn === "user__last_login") {
          // null values last for asc, beginning for desc
          if (aVal === null) return sortDirection === "asc" ? -1 : 1;
          if (bVal === null) return sortDirection === "asc" ? 1 : -1;

          const dateA = new Date(aVal);
          const dateB = new Date(bVal);

          return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
        }

        // default sort for non-timestamp columns
        return sortDirection === "asc"
          ? aVal.toString().localeCompare(bVal.toString(), "en", { numeric: true })
          : bVal.toString().localeCompare(aVal.toString(), "en", { numeric: true });
      });

      setUsers(newUsers);
    };

    sortUsers();
  }, [sortColumn, sortDirection])

  return (
    <div className="dashboard-table-container">
      <h3 className="dashboard-table-header-container">
        <button className="dashboard-toggle-table-view-button" onClick={() => handleSwitchTableView('analytics')}>Show Analytics</button>
        <div>User database</div>
        <div className="dashboard-table-header-buttons-container">
          <button className="dashboard-export-csv-button" onClick={() => exportUsersCSV(users, showDeactivatedAccounts)}>Export Table</button>
          <button className="dashboard-export-csv-button" onClick={() => copyEmails(users, showDeactivatedAccounts)}>Copy Emails</button>
          <div className="dashboard-toggle-deactivated-accounts">
            {deactivatedAccountsLoading ? (
              <div className="dashboard-spinner"></div>
            ) : (
              <input
                className="dashboard-toggle-checkbox"
                type="checkbox"
                id="show-deactivated-accounts"
                name="show-deactivated-accounts"
                checked={showDeactivatedAccounts}
                onChange={handleToggleDeactivatedAccounts} />
            )}
            <label htmlFor="show-deactivated-accounts">Show deactivated accounts</label>
          </div>
        </div>
      </h3>
      <table className="dashboard-table">
        <thead>
          <tr>
            <th className="dashboard-table-sortable-th" onClick={() => handleSort('user__email')}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <span>Email</span>
                <span>{displaySortArrow('user__email')}</span>
              </div>
            </th>
            <th className="dashboard-table-sortable-th" onClick={() => handleSort('name')}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <span>Name</span>
                <span>{displaySortArrow('name')}</span>
              </div>
            </th>
            <th className="dashboard-table-sortable-th" onClick={() => handleSort('news_organization')}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <span>Org</span>
                <span>{displaySortArrow('news_organization')}</span>
              </div>
            </th>
            <th className="dashboard-table-sortable-th">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <span>
                  <span
                    onClick={() => handleStatusSort("user__is_active")}
                    style={{
                      textDecoration: statusColumn === "user__is_active" ? 'underline' : 'none',
                    }}
                  >
                    Status
                  </span>
                  <span> | </span>
                  <span
                    onClick={() => handleStatusSort("user__last_login")}
                    style={{
                      textDecoration: statusColumn === "user__last_login" ? 'underline' : 'none',
                    }}
                  >
                    Last Activity
                  </span>
                </span>
                <span>
                  {displaySortArrow(statusColumn)}
                </span>
              </div>
            </th>
            <th className="dashboard-table-sortable-th" onClick={() => handleSort('user__is_staff')}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <span>Role</span>
                <span>{displaySortArrow('user__is_staff')}</span>
              </div>
            </th>
            <th className="dashboard-table-th">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users ? users.map((user, i) => {
            if (showDeactivatedAccounts === false && user.user__is_active === false) {
              return null;
            }
            return (
              <tr key={i} className={i % 2 === 0 ? "dashboard-table-tr-even" : ""}>
                <td className="dashboard-table-td">{user.user__email}</td>
                {renderEditableCell(i, 'name', user.name, user.user__id)}
                {renderEditableCell(i, 'news_organization', user.news_organization, user.user__id)}
                <td className={`dashboard-table-td ${user.user__is_active ? '' : 'dashboard-user-db-td-inactive'}`}>
                  {user.user__is_active ? 'active' : 'deactivated'}
                  <br />
                  {displayLastLogin(user.user__last_login)}
                </td>
                <td className={`dashboard-table-td ${user.user__is_staff ? 'dashboard-user-db-td-admin' : ''}`}>
                  {user.user__is_staff ? 'admin' : 'non-admin'}
                </td>
                <td className="dashboard-table-td">
                  <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    gap: '5px'
                  }}>
                    <button
                      onClick={() => handleToggleActive(user.user__id, user.user__is_active)}
                      className={user.user__is_active ? "dashboard-deactivate-button" : "dashboard-activate-button"}
                      disabled={loadingUserActive === user.user__id} // disable while loading
                    >
                      {loadingUserActive === user.user__id ? (
                        <div className="dashboard-spinner"></div>
                      ) : (
                        user.user__is_active ? "Deactivate" : "Activate"
                      )}
                    </button>
                    {isSuperuser ?
                      <button onClick={() => handleToggleAdmin(user.user__id, user.user__is_staff)}
                        className={user.user__is_staff ? "dashboard-remove-admin-button" : "dashboard-make-admin-button"}
                        disabled={loadingUserAdmin === user.user__id} // disable while loading
                      >
                        {loadingUserAdmin === user.user__id ? (
                          <div className="dashboard-spinner"></div>
                        ) : (
                          user.user__is_staff ? "Remove admin" : "Make admin"
                        )}
                      </button>
                      : null}
                  </div>
                </td>
              </tr>
            )
          }) : null}
        </tbody>
      </table>
    </div>
  );
};